import axios from "axios";
import type { z } from "zod";

import instance from "~/v2/api";
import { looseArray, looseParse } from "~/v2/api/looseParse";
import { watchlistSchema } from "~/v2/api/rest/account.schema";

export type Watchlist = z.infer<typeof watchlistSchema>;

export const watchlistsQueryKeys = {
  all: ["v3-poc", "watchlists"] as const,
  list: (accountId: string) =>
    [...watchlistsQueryKeys.all, "list", accountId] as const,
  detail: (accountId: string, watchlistId: string) =>
    [...watchlistsQueryKeys.all, "detail", accountId, watchlistId] as const,
};

export const getWatchlistsPath = (accountId: string): string =>
  `/accounts/${accountId}/watchlists`;

export const getWatchlistPath = (
  accountId: string,
  watchlistId: string,
): string => `/accounts/${accountId}/watchlists/${watchlistId}`;

export const membershipSymbols = (watchlist: Watchlist): string[] =>
  (watchlist.assets ?? []).map((asset) => asset.symbol);

export const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

export const getWatchlists = async ({
  accountId,
}: {
  accountId: string;
}): Promise<Watchlist[]> => {
  const response = await instance.get(getWatchlistsPath(accountId));
  return looseParse(
    looseArray(watchlistSchema, "v3.getWatchlists"),
    response.data,
    "v3.getWatchlists",
  );
};

export const getWatchlist = async ({
  accountId,
  watchlistId,
}: {
  accountId: string;
  watchlistId: string;
}): Promise<Watchlist> => {
  const response = await instance.get(getWatchlistPath(accountId, watchlistId));
  return looseParse(watchlistSchema, response.data, "v3.getWatchlist");
};

export const createWatchlist = async ({
  accountId,
  name,
}: {
  accountId: string;
  name: string;
}): Promise<Watchlist> => {
  const response = await instance.post(getWatchlistsPath(accountId), {
    name,
    symbols: [],
  });
  return looseParse(watchlistSchema, response.data, "v3.createWatchlist");
};

export const addWatchlistSymbol = async ({
  accountId,
  watchlistId,
  symbol,
}: {
  accountId: string;
  watchlistId: string;
  symbol: string;
}): Promise<Watchlist> => {
  const response = await instance.post(
    getWatchlistPath(accountId, watchlistId),
    { symbol },
  );
  return looseParse(watchlistSchema, response.data, "v3.addWatchlistSymbol");
};

export const renameWatchlist = async ({
  accountId,
  watchlistId,
  name,
  symbols,
}: {
  accountId: string;
  watchlistId: string;
  name: string;
  symbols: string[];
}): Promise<Watchlist> => {
  const response = await instance.put(
    getWatchlistPath(accountId, watchlistId),
    { name, symbols },
  );
  return looseParse(watchlistSchema, response.data, "v3.renameWatchlist");
};
